// SP1 (Mail-Topbar): pure Hilfslogik der globalen Mail-Suche — bewusst aus
// MailView.tsx herausgelöst, damit sie in der Vitest-Suite testbar ist.

import type { MailFolder } from "../../services/graphMailService";

/**
 * Die Suche ist IMMER global: Ziel sind alle beim User lesbaren Postfächer
 * (die frühere „Alle Postfächer"-Checkbox ist entfallen). Fallback, solange
 * keine Freigaben geladen sind, ist das aktuell geöffnete Postfach.
 */
export function resolveSearchTargets(
    readableMailboxes: string[],
    currentMailbox: string | null | undefined,
): string[] {
    if (readableMailboxes.length > 0) return readableMailboxes;
    return currentMailbox ? [currentMailbox] : [];
}

/** Kurzlabel für den Quell-Postfach-Chip („cbb" statt „cbb@courte.com"). */
export function mailboxChipLabel(mailbox: string): string {
    const local = mailbox.split("@")[0];
    return local || mailbox;
}

/**
 * Mail-SP2 (Ordner-Chip): baut aus dem (max. 3 Ebenen tiefen) Ordnerbaum eines
 * Postfachs eine Lookup-Map `Ordner-Id → Anzeigename` für die Suchtreffer.
 *
 * Der POSTEINGANG wird BEWUSST AUSGELASSEN — Suchtreffer aus der Inbox tragen
 * keinen Ordner-Chip. Die Inbox wird ausschließlich über
 * `wellKnownName === 'inbox'` (die Inbox-Ordner-Id) erkannt, NIE über den
 * `displayName` (englische Locales heißen „Inbox"). Unterordner des
 * Posteingangs bleiben auflösbar.
 *
 * Ordner, die (noch) nicht im geladenen Baum stehen, fehlen in der Map → der
 * Aufrufer lässt den Chip weg und zeigt NIE die rohe Graph-Id.
 */
export function buildFolderNameMap(tree: MailFolder[]): Map<string, string> {
    const map = new Map<string, string>();
    const walk = (nodes: MailFolder[]): void => {
        for (const n of nodes) {
            if (n.wellKnownName !== "inbox" && n.id && n.displayName) {
                map.set(n.id, n.displayName);
            }
            if (n.children?.length) walk(n.children);
        }
    };
    walk(tree);
    return map;
}

/**
 * Vg-SP5 (Auto-Archiv im Vorgang-Mail-Picker): Set der Ordner-Ids EINES
 * Postfachs, die der Posteingang sind (`wellKnownName === 'inbox'`) — das
 * Gegenstück zu {@link buildFolderNameMap}, die die Inbox bewusst auslässt.
 *
 * Damit lässt sich zu einem Suchtreffer über seine `parentFolderId` + das
 * Postfach entscheiden, ob er im Posteingang liegt (nur dann darf das optionale
 * Auto-Archiv greifen). Ist der Ordnerbaum (noch) nicht geladen, ist das Set
 * leer → der Aufrufer behandelt „unbekannt" KONSERVATIV als NICHT-Posteingang
 * und archiviert die Nachricht nicht.
 */
export function collectInboxFolderIds(tree: MailFolder[]): Set<string> {
    const ids = new Set<string>();
    const walk = (nodes: MailFolder[]): void => {
        for (const n of nodes) {
            if (n.wellKnownName === "inbox" && n.id) ids.add(n.id);
            if (n.children?.length) walk(n.children);
        }
    };
    walk(tree);
    return ids;
}
